/**
 * 任意テキスト / SSML の音声プレビューの副作用を担うフック。
 *
 * 分割前は `FreeTextFields` 内のローカル state (`previewing` / `previewError` / `previewUrl`)、
 * `previewAudioRef`、および再生速度反映の `useEffect` として実装されていたものを、
 * ロジックを変えずそのまま切り出したもの。
 *
 * 要件 2: プレビュー音声にも再生速度を反映するため、<audio> 要素に ref を張り、
 * playbackRate プロパティ変化時に手動で設定する。src が変わったときや
 * 再生開始時にリセットされてしまうことがあるため、呼び出し側の `onLoadedMetadata` でも
 * 再適用する (ref + onLoadedMetadata の両方で反映)。
 */
import { useEffect, useRef, useState } from 'react';

import type { PreviewSsmlHandler } from '../types';

export interface UseAudioPreviewResult {
  /** プレビュー音声の生成中フラグ */
  previewing: boolean;
  /** プレビュー生成失敗時のエラーメッセージ (null = エラーなし) */
  previewError: string | null;
  /** 生成済みプレビュー音声の object URL (blob:)。null = 未生成 */
  previewUrl: string | null;
  /** プレビュー <audio> 要素への ref (playbackRate を手動設定するために使う) */
  previewAudioRef: React.RefObject<HTMLAudioElement | null>;
  /** 実効再生速度 (範囲 0.5〜2.0 外 / 未指定は 1.0 = 等倍) */
  effectivePlaybackRate: number;
  /** プレビュー音声を生成する (呼び出し側で入力内容とプレビュー可否を判定してから呼ぶ) */
  startPreview: (input: Parameters<PreviewSsmlHandler>[0]) => Promise<void>;
}

export function useAudioPreview({
  onPreviewSsml,
  playbackRate,
}: {
  onPreviewSsml?: PreviewSsmlHandler;
  /**
   * 親コンテンツの `playback_rate` (要件 2)。
   * `undefined` / `1` は等倍再生。範囲 (0.5〜2.0) 外の値は 1.0 に落として無視する。
   */
  playbackRate?: number;
}): UseAudioPreviewResult {
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const effectivePlaybackRate =
    typeof playbackRate === 'number' && playbackRate >= 0.5 && playbackRate <= 2.0
      ? playbackRate
      : 1.0;
  useEffect(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.playbackRate = effectivePlaybackRate;
    }
  }, [effectivePlaybackRate, previewUrl]);

  const startPreview = async (input: Parameters<PreviewSsmlHandler>[0]): Promise<void> => {
    if (!onPreviewSsml) return;
    setPreviewError(null);
    setPreviewing(true);
    try {
      // 直前のプレビュー URL は解放しておく
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const url = await onPreviewSsml(input);
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError((err as Error).message || '音声プレビューの生成に失敗しました');
    } finally {
      setPreviewing(false);
    }
  };

  return {
    previewing,
    previewError,
    previewUrl,
    previewAudioRef,
    effectivePlaybackRate,
    startPreview,
  };
}
